import { supabase } from '../supabaseClient';

// One source for the "waiting on you" data (waiting-on-you brief §4) — the two
// things a supplier is waiting for across live briefs. Both the Briefs banner /
// tab count AND the tab's two group tables read from these, so the counts can't
// disagree (fourth de-dup after the compliance rollup, response counters and the
// overdue-days discrepancy).
//
// A row is only "waiting" while its brief is live (status 'sent') — a decided
// brief isn't waiting on anyone.

const LIVE = 'sent';

// Unanswered supplier questions on live briefs.
export async function fetchWaitingQuestions() {
  const { data } = await supabase
    .from('brief_questions')
    .select('id, body, created_at, brief_id, suppliers(supplier_name), briefs(id, brief_name, fusion_project_number, status, required_response_time)')
    .eq('status', 'open')
    .order('created_at', { ascending: true });
  return (data || []).filter((q) => q.briefs && q.briefs.status === LIVE);
}

// Outstanding post-deadline update requests on live briefs.
export async function fetchWaitingUpdateRequests() {
  const { data } = await supabase
    .from('brief_update_requests')
    .select('id, message, created_at, brief_id, suppliers(supplier_name), briefs(id, brief_name, fusion_project_number, status, required_response_time)')
    .is('resolved_at', null)
    .order('created_at', { ascending: false });
  return (data || []).filter((u) => u.briefs && u.briefs.status === LIVE);
}

// Suppliers who have asked for more time on a live brief.
//
// Unlike questions and update requests this is not its own table — it's a pair
// of columns on brief_recipients (2026-06-extension-requests), set when the
// supplier asks and cleared when the deadline is actually moved or the brief is
// reopened. So "outstanding" is simply the flag still being set.
//
// The status filter matches what BriefDetail already shows against a recipient:
// only pending and accepted. A supplier who declined or already submitted has
// nothing to gain from more time, and their flag would otherwise sit in the
// queue forever with no action that clears it.
export async function fetchWaitingExtensionRequests() {
  const { data } = await supabase
    .from('brief_recipients')
    .select('id, extension_requested_at, extension_request_note, extension_answered_at, brief_id, status, suppliers(supplier_name), briefs(id, brief_name, fusion_project_number, status, required_response_time)')
    .not('extension_requested_at', 'is', null)
    // Answered — refused outright, or countered with a different date. The
    // request stays on the record, because it did happen, but it is no longer
    // waiting on anyone. Granting exactly what was asked for goes through
    // Extend Deadline instead, which clears extension_requested_at outright, so
    // a granted one never reaches here either.
    .is('extension_answered_at', null)
    .in('status', ['pending', 'accepted'])
    .order('extension_requested_at', { ascending: false });
  return (data || []).filter((r) => r.briefs && r.briefs.status === LIVE);
}
